import { computed, type Ref, ref } from 'vue';
import { generateAdditionalBusinessContextSuggestions } from '@/api/personalSurveyProfile';
import type { PersonalSurveyProfileInput } from '@/types';
import {
  hasBusinessInfo,
  isSameBusinessInfo,
} from '@/utils/personalSurveyProfile';

export type AdditionalBusinessContextSuggestionStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'error';

export const useAdditionalBusinessContextSuggestion = (
  input: Ref<PersonalSurveyProfileInput>,
) => {
  const status = ref<AdditionalBusinessContextSuggestionStatus>('idle');
  const suggestions = ref<string[]>([]);
  const shownSuggestions = new Set<string>();
  const latestRequestId = ref(0);
  const generatedInput = ref<PersonalSurveyProfileInput>();

  const canRegenerate = computed(
    () => status.value !== 'loading' && hasBusinessInfo(input.value),
  );

  const fail = () => {
    suggestions.value = [];
    status.value = 'error';
  };

  const generate = async () => {
    latestRequestId.value += 1;
    const requestId = latestRequestId.value;
    status.value = 'loading';
    generatedInput.value = { ...input.value };
    try {
      const response = await generateAdditionalBusinessContextSuggestions(
        input.value,
        [...shownSuggestions],
      );
      if (requestId !== latestRequestId.value) return;
      if (response.suggestions.length === 0) {
        fail();
        return;
      }
      response.suggestions.forEach(s => shownSuggestions.add(s));
      suggestions.value = response.suggestions;
      status.value = 'loaded';
    } catch {
      if (requestId !== latestRequestId.value) return;
      fail();
    }
  };

  const generateIfStale = () => {
    if (status.value === 'loading') return;
    if (!hasBusinessInfo(input.value)) return;
    const isUpToDate =
      status.value === 'loaded' &&
      generatedInput.value !== undefined &&
      isSameBusinessInfo(generatedInput.value, input.value);
    if (isUpToDate) return;
    generate();
  };

  return { status, suggestions, canRegenerate, generate, generateIfStale };
};
